/**
 * Operator role gate.
 *
 * On mount it calls GET /v1/admin/me (via TanStack Query):
 *   - 401/403 → the full-page <AccessDenied/> screen (the account lacks the
 *     admin/support operator role; the backend is the authority);
 *   - other failures → a retryable error screen (network/5xx);
 *   - success → the outlet renders with the role exposed through
 *     <AdminRoleProvider/> (useAdminRole()).
 *
 * The client-side role is presentation only; every admin route re-checks the
 * role server-side on each request.
 */
import { Outlet } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getAdminMe } from '@/api/admin';
import { adminKeys } from '@/api/queryKeys';
import { isApiError } from '@/api/errors';
import { AccessDenied } from './AccessDenied';
import { AdminRoleProvider } from './AdminRoleContext';
import { LoadingState } from '@/components/feedback/LoadingState';
import { Button } from '@/components/ui/button';
import { AlertCircle } from '@/components/icons';

export function RequireRole() {
  const me = useQuery({
    queryKey: adminKeys.me,
    queryFn: ({ signal }) => getAdminMe(signal),
    // The operator role does not change during a session; a refetch only
    // happens on window refocus/reconnect per the client defaults.
    staleTime: 5 * 60_000,
  });

  if (me.isPending) {
    return <LoadingState label="Checking operator role…" />;
  }

  if (me.isError) {
    if (isApiError(me.error) && (me.error.status === 401 || me.error.status === 403)) {
      return <AccessDenied />;
    }
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div
          role="alert"
          className="w-full max-w-md rounded-lg border bg-surface p-6 shadow-sm text-center"
        >
          <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-warning/10 text-warning">
            <AlertCircle className="h-5 w-5" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-lg font-semibold text-foreground">Could not verify your role</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            The admin API did not answer the role check. This is usually transient — try again.
          </p>
          <Button variant="outline" className="mt-6" onClick={() => void me.refetch()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <AdminRoleProvider value={{ role: me.data.role, clerkUserID: me.data.clerk_user_id }}>
      <Outlet />
    </AdminRoleProvider>
  );
}
